import React, { useCallback, useContext, useEffect, useState } from "react";
import { Switch, Tooltip, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { InfoOutlined } from "@material-ui/icons";
import { useSnackbar } from "notistack";

import { FullSite } from "../../../../dtos/site";
import { SiteCommissioningSummary } from "../../../../dtos/siteCommissioning";
import { AjaxResult } from "../../../../enums/ajaxResult";
import { useApi } from "../../../useApi";
import { useForceRerender } from "../../../../hooks/useForceRerender";
import SignalRContext from "../../../../signalr/context";
import { tokens } from "../../sites/SiteConfigPage/console/tokens";
import { HardwareConfigurationSummaryPanel } from "../../sites/SiteConfigPage/HardwareConfigurationSummaryPanel";

import { SiteRoomAlarmIndicator } from "./SiteRoomAlarmIndicator";
import { SiteRoomStatusIndicator } from "./SiteRoomStatusIndicator";
import { SiteServiceStatusIndicator } from "./SiteServiceStatusIndicator";
import { EnableMaintenanceModeDialog } from "../../sites/EnableMaintenanceModeDialog";

interface StatusCardStyleProps {
    alarmActive: boolean;
}

const useStyles = makeStyles(() => ({
    statusCard: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: "100%",
        boxSizing: "border-box",
        padding: "20px 24px",
        backgroundColor: tokens.surface,
        borderRadius: "12px",
        border: `1px solid ${tokens.line}`,
        borderTop: "4px solid",
        borderTopColor: (props: StatusCardStyleProps) => (props.alarmActive ? tokens.danger : tokens.ok),
        boxShadow: tokens.shadow,
        "@media (max-width: 600px)": {
            padding: "16px",
            borderRadius: "8px",
        },
    },
    identityRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        flexWrap: "wrap",
        gap: "8px",
    },
    identityText: {
        display: "flex",
        flexDirection: "column",
    },
    statusRow: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "12px",
    },
    maintenanceControl: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 12px 4px 4px",
        borderRadius: "20px",
        border: `1px solid ${tokens.line}`,
    },
    maintenanceControlActive: {
        borderColor: tokens.wait,
        backgroundColor: tokens.waitBg,
    },
}));

interface Props {
    site: FullSite;
    dealerId: number | null;
    commissioningSummary?: SiteCommissioningSummary;
    showHardwareSummary: boolean;
}

export function SiteInfo({ site, dealerId, commissioningSummary, showHardwareSummary }: Props) {
    const { maintenanceModeApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [showDialog, setShowDialog] = useState(false);
    const [isInMaintenanceMode, setIsInMaintenanceMode] = useState(false);
    const [processing, setProcessing] = useState(false);

    // alarmStatus/maintenanceModeStatus are map references that don't change identity on update.
    useForceRerender(2000);
    const { alarmStatus, maintenanceModeStatus } = useContext(SignalRContext);
    const dynamicAlarm = alarmStatus.get(site.id);
    const isAlarmActive = dynamicAlarm !== undefined ? dynamicAlarm : site.activeAlarm;

    const classes = useStyles({ alarmActive: isAlarmActive });

    useEffect(() => {
        maintenanceModeApi.getStatus(site.id).then((r) => {
            if (r.result === AjaxResult.Success && r.data) {
                setIsInMaintenanceMode(r.data.isInMaintenanceMode);
            }
        });
    }, [site.id, maintenanceModeApi]);

    useEffect(() => {
        const signalRStatus = maintenanceModeStatus.get(site.id);
        if (signalRStatus !== undefined) {
            setIsInMaintenanceMode(signalRStatus.isEnabled);
        }
    }, [maintenanceModeStatus, site.id]);

    const handleDisable = useCallback(() => {
        if (!window.confirm("Are you sure you want to disable maintenance mode?")) {
            return;
        }
        setProcessing(true);
        maintenanceModeApi.disable(site.id)
            .then((r) => {
                if (r.result === AjaxResult.Success) {
                    setIsInMaintenanceMode(false);
                    enqueueSnackbar("Maintenance mode disabled", { variant: "success" });
                } else {
                    const msg = r.messages?.join(", ") ?? "Failed to disable maintenance mode";
                    enqueueSnackbar(msg, { variant: "error" });
                }
            })
            .catch(() => enqueueSnackbar("Failed to disable maintenance mode", { variant: "error" }))
            .finally(() => setProcessing(false));
    }, [maintenanceModeApi, site.id, enqueueSnackbar]);

    const handleToggle = useCallback((_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        if (checked) {
            setShowDialog(true);
        } else {
            handleDisable();
        }
    }, [handleDisable]);

    return (
        <>
            <div className={classes.statusCard}>
                <div className={classes.identityRow}>
                    <div className={classes.identityText}>
                        <Typography variant="h6">{site.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                            ID {site.uniqueSiteId} · v{site.acsVersion}
                        </Typography>
                    </div>
                    <SiteServiceStatusIndicator siteId={site.id} />
                </div>

                <div className={classes.statusRow}>
                    <SiteRoomAlarmIndicator site={site} dealerId={dealerId} />
                    <SiteRoomStatusIndicator siteId={site.id} />

                    <div
                        className={
                            isInMaintenanceMode
                                ? `${classes.maintenanceControl} ${classes.maintenanceControlActive}`
                                : classes.maintenanceControl
                        }
                    >
                        <Switch
                            color="primary"
                            size="small"
                            checked={isInMaintenanceMode}
                            disabled={processing}
                            onChange={handleToggle}
                        />
                        <Typography variant="body2">
                            Maintenance Mode{isInMaintenanceMode ? " — active" : ""}
                        </Typography>
                        <Tooltip title="Enable to temporarily suppress email notifications while performing service work or testing to avoid triggering alerts for customers">
                            <InfoOutlined style={{ fontSize: "1rem", opacity: 0.5, cursor: "help" }} />
                        </Tooltip>
                    </div>
                </div>
            </div>

            {showHardwareSummary && <HardwareConfigurationSummaryPanel summary={commissioningSummary} />}

            <EnableMaintenanceModeDialog
                open={showDialog}
                onClose={() => setShowDialog(false)}
                siteId={site.id}
                onSuccess={() => setIsInMaintenanceMode(true)}
            />
        </>
    );
}
